/**
 * Dashboard number/currency/delta formatters. All use the Greek locale (`el-GR`)
 * per 11-dashboard-spec.md — so thousands use `.`, decimals use `,`, and EUR is
 * suffixed (e.g. `34.592,18 €`).
 */

import { formatDistanceToNowStrict } from 'date-fns';
import { el } from 'date-fns/locale';

const LOCALE = 'el-GR';

const currencyFmt = new Intl.NumberFormat(LOCALE, {
	style: 'currency',
	currency: 'EUR',
	minimumFractionDigits: 2,
	maximumFractionDigits: 2
});

const integerFmt = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 });

/** `34.592,18 €` */
export function formatCurrency(n: number): string {
	return currencyFmt.format(n);
}

/** `1.234` (thousands separator, no decimals) */
export function formatInteger(n: number): string {
	return integerFmt.format(n);
}

/** `3,7%` (default 1 decimal) */
export function formatPercent(n: number, decimals = 1): string {
	const fmt = new Intl.NumberFormat(LOCALE, {
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals
	});
	return `${fmt.format(n)}%`;
}

export type DeltaDirection = 'up' | 'down' | 'flat';

export interface FormattedDelta {
	/** Signed string incl. unit, e.g. `+12,4%` or `+0,4pp` (used by the trend tooltip). */
	text: string;
	/** Unsigned string incl. unit, e.g. `12,4%` (KPI tiles pair this with a direction arrow). */
	magnitude: string;
	direction: DeltaDirection;
}

/**
 * Format a delta for a KPI tile. `asPP` switches the unit from percent to
 * percentage points (used by the conversion-rate KPI, where the delta is the raw
 * `curr - prev` difference, not a percentage change).
 */
export function formatDelta(value: number, asPP = false): FormattedDelta {
	const direction: DeltaDirection = value > 0 ? 'up' : value < 0 ? 'down' : 'flat';
	const sign = value > 0 ? '+' : value < 0 ? '−' : '';
	const magnitudeFmt = new Intl.NumberFormat(LOCALE, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
	const magnitude = magnitudeFmt.format(Math.abs(value));
	const unit = asPP ? 'pp' : '%';
	return { text: `${sign}${magnitude}${unit}`, magnitude: `${magnitude}${unit}`, direction };
}

/** `πριν 2 λεπτά` — relative time from now, Greek locale (live indicator + alert ages). */
export function formatRelativeFromNow(date: string | number | Date): string {
	return formatDistanceToNowStrict(new Date(date), { addSuffix: true, locale: el });
}

/** Compact currency for chart axes, e.g. `€1,2χ` (thousands) — falls back to full for < 1000. */
export function formatCurrencyCompact(n: number): string {
	if (Math.abs(n) < 1000) return formatCurrency(n);
	const fmt = new Intl.NumberFormat(LOCALE, {
		style: 'currency',
		currency: 'EUR',
		notation: 'compact',
		maximumFractionDigits: 1
	});
	return fmt.format(n);
}
